/**
 * Reorder a model picker. The first model is the default: preselected in the app and
 * used by the gateway for a request naming no model (CLAUDE-DOC:MODEL_BEHAVIOUR).
 */
import React, { useEffect, useState } from 'react';
import { AdminApiError, reorderModelPicker } from '../../utils/adminApi';

interface PickerOrderPanelProps {
  idToken: string;
  picker: string;
  /** The picker's models in their saved order. */
  modelIds: string[];
  nameOf: (modelId: string) => string;
  /** Disabled models can't be the default: the server refuses that order. */
  isDisabled: (modelId: string) => boolean;
  onSaved: () => Promise<void>;
}

export const PickerOrderPanel: React.FC<PickerOrderPanelProps> = ({
  idToken,
  picker,
  modelIds,
  nameOf,
  isDisabled,
  onSaved,
}) => {
  const [order, setOrder] = useState(modelIds);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const savedKey = modelIds.join(',');
  // The saved order last taken in. It lags `modelIds` by one render, which lets the effect
  // tell a local edit from a saved order changed elsewhere.
  const [lastSavedKey, setLastSavedKey] = useState(savedKey);
  const changed = order.join(',') !== lastSavedKey;
  // Take the saved order when it changes on the server — but never over an
  // unsaved order the admin is still arranging here.
  useEffect(() => {
    if (savedKey === lastSavedKey) return;
    setLastSavedKey(savedKey);
    if (!changed) setOrder(modelIds);
  }, [savedKey, lastSavedKey, changed, modelIds]);

  const move = (index: number, by: -1 | 1) => {
    setOrder((current) => {
      const next = [...current];
      [next[index], next[index + by]] = [next[index + by], next[index]];
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await reorderModelPicker(idToken, picker, order);
      await onSaved();
    } catch (err) {
      setError(
        err instanceof AdminApiError && err.problems.length > 0
          ? err.problems.join('; ')
          : err instanceof Error
            ? err.message
            : 'Failed to save the order'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-gray-900/60 border border-gray-700 rounded p-4">
      <h3 className="text-white font-semibold mb-2 font-mono text-sm">
        {picker}
      </h3>
      <ol className="space-y-1 text-sm">
        {order.map((id, index) => (
          <li key={id} className="flex items-center gap-2 text-gray-200">
            <span className="w-5 text-right text-gray-500">{index + 1}.</span>
            <span className="flex-1">
              {nameOf(id)}
              {index === 0 && (
                <span className="ml-2 text-xs text-emerald-300">default</span>
              )}
              {isDisabled(id) && (
                <span className="ml-2 text-xs text-red-300">disabled</span>
              )}
            </span>
            <button
              type="button"
              onClick={() => move(index, -1)}
              disabled={index === 0 || saving}
              aria-label={`Move ${nameOf(id)} up`}
              className="px-2 rounded bg-gray-700 hover:bg-gray-600 disabled:opacity-30"
            >
              ↑
            </button>
            <button
              type="button"
              onClick={() => move(index, 1)}
              disabled={index === order.length - 1 || saving}
              aria-label={`Move ${nameOf(id)} down`}
              className="px-2 rounded bg-gray-700 hover:bg-gray-600 disabled:opacity-30"
            >
              ↓
            </button>
          </li>
        ))}
      </ol>
      {error && (
        <div role="alert" className="mt-2 text-xs text-red-300">
          {error}
        </div>
      )}
      {changed && (
        <div className="mt-3 flex justify-end gap-2">
          <button
            type="button"
            onClick={() => {
              setOrder(modelIds);
              setError(null);
            }}
            disabled={saving}
            className="px-3 py-1.5 rounded bg-gray-700 hover:bg-gray-600 text-xs text-gray-100"
          >
            Reset
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="px-3 py-1.5 rounded bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-xs font-semibold text-white"
          >
            {saving ? 'Saving…' : 'Save order'}
          </button>
        </div>
      )}
    </div>
  );
};
